import React, { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import {
  getInstallPrompt,
  isAppInstalled,
  isIosSafari,
  showInstallPrompt,
  subscribeInstallPrompt,
} from "../../common/pwaInstall";

// The installed window has to open straight on the dashboard, so while this
// page is mounted the document points at a manifest whose start_url says so.
const OWNER_MANIFEST = "/owner-manifest.json";
const DISMISS_KEY = "ownerInstallDismissed";

const InstallAppButton = () => {
  const [prompt, setPrompt] = useState(getInstallPrompt());
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "true"
  );
  const [showIosHint, setShowIosHint] = useState(false);

  // Swapping the manifest link makes Chrome re-read it (and re-offer the
  // install), so it has to happen before the owner reaches for the button.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return undefined;

    const previous = link.getAttribute("href");
    link.setAttribute("href", OWNER_MANIFEST);

    return () => {
      if (previous) link.setAttribute("href", previous);
    };
  }, []);

  useEffect(() => subscribeInstallPrompt(setPrompt), []);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "true");
    setDismissed(true);
  };

  const handleInstall = async () => {
    if (!prompt) {
      // iOS: no API to call, only instructions.
      setShowIosHint((open) => !open);
      return;
    }

    await showInstallPrompt();
  };

  // Nothing to offer when it is already installed, dismissed, or the browser
  // never signalled that it can install (and is not iOS Safari).
  if (dismissed || isAppInstalled()) return null;
  if (!prompt && !isIosSafari()) return null;

  return (
    <div className="fixed bottom-5 right-4 z-40 flex flex-col items-end gap-2 sm:bottom-6 sm:right-6">
      {showIosHint && (
        <div className="max-w-[240px] rounded-xl border border-gray-200 bg-white p-3 text-xs leading-relaxed text-gray-700 shadow-lg">
          Open the <Share size={12} className="inline -mt-0.5" /> Share menu and
          tap <span className="font-semibold">Add to Home Screen</span> to install
          the dashboard.
        </div>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Hide install app button"
          className="absolute -top-3 -right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-md transition-colors hover:bg-gray-100 hover:text-gray-700"
        >
          <X size={14} />
        </button>

        <button
          type="button"
          onClick={handleInstall}
          className="inline-flex items-center gap-2 rounded-full bg-[#503691] px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:bg-[#3f2a73] hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#503691] focus-visible:ring-offset-2"
        >
          <Download size={17} />
          Install App
        </button>
      </div>
    </div>
  );
};

export default InstallAppButton;
